// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// PE PROFIT — TYPEN (Rentabilitätsvorschau / Prognose)
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

export interface Dienstleistungserweiterung {
    id: string;
    name: string;
    jahresumsatzNetto: number;
    startMonat: number;
    aktiv: boolean;
}

export interface Investition {
    id: string;
    name: string;
    monatskosten: number;
    laufzeitMonate: number;
    aktiv: boolean;
}

export const PE_PROFIT_NEUER_MITARBEITER_ID = '__neu__';
/** UI-Placeholder: neue freie Betriebskostenart (noch ohne BWA-Posten) */
export const PE_PROFIT_NEUE_KOSTENART_ID = '__neue_kosten__';

export const PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_NEU = 2.5;
export const PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_BISHER = 2.8;
export const PE_PROFIT_DEFAULT_UMSATZ_AUFFANG_PROZENT = 100;

export interface PersonalAnpassung {
    id: string;
    mitarbeiterId: string;
    mitarbeiterName: string;
    /** Monatlicher IST-Wert zum Zeitpunkt der Anpassung */
    istWert: number;
    /** Monatlicher Zielwert (Wunschwert); bei Entlassung = 0 */
    neuerZielwert: number;
    aktiv: boolean;
    /** Nur Neuer Mitarbeiter: Zusatzumsatz = Gehalt × Faktor */
    umsatzFaktorNeu?: number;
    /** Mitarbeiter entlassen/freistellen — Personalkosten-Prognose = 0 */
    entlassen?: boolean;
    /** Bei Entlassung: bisheriger Umsatz = IST-Gehalt × Faktor */
    bisherigerUmsatzFaktor?: number;
    /** Bei Entlassung: wie viel % des bisherigen Umsatzes aufgefangen werden (0–100) */
    umsatzAuffangProzent?: number;
}

export interface GemeinkostenDetailAnpassung {
    id: string;
    bwaKey: string;
    label: string;
    /** Monatlicher IST-Wert zum Zeitpunkt der Anpassung */
    istWert: number;
    /** Monatlicher Zielwert (Wunschwert) */
    neuerZielwert: number;
    /** Optionale Bank-Notiz (z. B. Zweck der Kostenerhöhung) */
    notiz?: string;
}

export interface Unterschrift {
    ort: string;
    datum: string;
    unternehmerName: string;
    bestaetigt: boolean;
}

export type PEProfitAnsichtsModus = 'monat' | 'jahr';

/** Beratungspraxis: 20 % Preiserhöhung ≈ 5 % Kundenschwund (Verhältnis 4:1). */
export const calcAutoKundenVerlustProzent = (preisAnpassungProzent: number): number =>
    (preisAnpassungProzent || 0) / 4;

export interface PEProfitPrognose {
    preisAnpassungProzent: number;
    /** null = automatisch (¼ der Preiserhöhung); sonst manueller Kundenschwund in % */
    kundenVerlustProzent: number | null;
    /** @deprecated Legacy — nur Fallback wenn materialZielMonat null */
    materialKostenAnpassungProzent: number;
    /** Monatlicher Ziel-Wareneinsatz in €; null = IST-Quote auf Prognose-Umsatz */
    materialZielMonat: number | null;
    /** Begründung zur Materialkosten-Änderung (Bank / KI) */
    materialNotiz: string;
    /**
     * Szenario-Override Unternehmerlohn/Entnahme (Monat netto €).
     * @deprecated UI entfernt — Prognose nutzt immer IST aus Unternehmerwerte.
     */
    unternehmerLohnZielMonat: number | null;
    /**
     * Monats-Delta des Unternehmer-Eigenumsatzes (netto €).
     * Positiv = mehr, negativ = weniger als IST-Unternehmerumsatz.
     * Wird immer auf den Prognose-Gesamtumsatz angerechnet (ggf. mit Team-Auffang).
     */
    unternehmerUmsatzDeltaMonat: number;
    /**
     * @deprecated Immer wirksam — Checkbox entfernt; Delta wird stets angerechnet.
     */
    unternehmerUmsatzAufGesamtAnrechnen: boolean;
    /**
     * Bei negativem Umsatz-Delta: wie viel % des Rückgangs fängt das Team auf (0–100).
     * 100 = kein Netto-Verlust am Gesamtumsatz; 0 = voller Abzug.
     */
    unternehmerUmsatzAuffangProzent: number;
    neueDienstleistungen: Dienstleistungserweiterung[];
    gemeinkostenDetailAnpassungen: GemeinkostenDetailAnpassung[];
    neueInvestitionen: Investition[];
    personalAnpassungen: PersonalAnpassung[];
    szenarioName: string;
    ansichtsModus: PEProfitAnsichtsModus;
    unterschrift: Unterschrift;
}

export const createEmptyUnterschrift = (): Unterschrift => ({
    ort: '',
    datum: '',
    unternehmerName: '',
    bestaetigt: false,
});

export const createDefaultPEProfitPrognose = (): PEProfitPrognose => ({
    preisAnpassungProzent: 0,
    kundenVerlustProzent: null,
    materialKostenAnpassungProzent: 0,
    materialZielMonat: null,
    materialNotiz: '',
    unternehmerLohnZielMonat: null,
    unternehmerUmsatzDeltaMonat: 0,
    unternehmerUmsatzAufGesamtAnrechnen: true,
    unternehmerUmsatzAuffangProzent: PE_PROFIT_DEFAULT_UMSATZ_AUFFANG_PROZENT,
    neueDienstleistungen: [],
    gemeinkostenDetailAnpassungen: [],
    neueInvestitionen: [],
    personalAnpassungen: [],
    szenarioName: 'Basis-Prognose',
    ansichtsModus: 'monat',
    unterschrift: createEmptyUnterschrift(),
});

/** True wenn Coach/Mandant bereits gespeicherte Prognose-Anpassungen hat. */
export const hasPersistedPeProfitPrognose = (prognose?: PEProfitPrognose | null): boolean => {
    if (!prognose) return false;

    if (prognose.preisAnpassungProzent !== 0) return true;
    if (prognose.kundenVerlustProzent !== null && prognose.kundenVerlustProzent !== 0) return true;
    if (prognose.materialKostenAnpassungProzent !== 0) return true;
    if (prognose.materialZielMonat !== null) return true;
    if ((prognose.unternehmerUmsatzDeltaMonat ?? 0) !== 0) return true;
    if ((prognose.gemeinkostenDetailAnpassungen ?? []).length > 0) return true;
    if ((prognose.personalAnpassungen ?? []).length > 0) return true;
    if ((prognose.neueDienstleistungen ?? []).some((item) => item.aktiv)) return true;
    if ((prognose.neueInvestitionen ?? []).some((item) => item.aktiv)) return true;

    const u = prognose.unterschrift;
    if (u?.bestaetigt) return true;
    if (u?.ort?.trim()) return true;
    if (u?.unternehmerName?.trim()) return true;

    return false;
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// PE PROFIT — BERECHNUNGS-ERGEBNIS (P&L-Schema)
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

export interface PEPnlBlock {
    umsatzNetto: number;
    wareneinsatz: number;
    rohertrag: number;
    personalkostenProduktiv: number;
    personalkostenUnproduktiv: number;
    betriebskosten: number;
    geraetekosten: number;
    /** Abschreibungen, Zinserträge, Sonstige Einnahmen (BWA-Pluswerte, mindern Sollkosten) */
    sonstigeEinnahmen: number;
    investitionen: number;
    gesamtkosten: number;
    betriebsergebnis: number;
    einkommensteuer: number;
    inhaberertrag: number;
    rentabilitaetProzent: number;
    materialquoteProzent: number;
}

export interface PEPnlAbweichung {
    umsatzNetto: number;
    wareneinsatz: number;
    rohertrag: number;
    personalkostenProduktiv: number;
    personalkostenUnproduktiv: number;
    betriebskosten: number;
    geraetekosten: number;
    /** Abschreibungen, Zinserträge, Sonstige Einnahmen (BWA-Pluswerte, mindern Sollkosten) */
    sonstigeEinnahmen: number;
    investitionen: number;
    gesamtkosten: number;
    betriebsergebnis: number;
    einkommensteuer: number;
    inhaberertrag: number;
    rentabilitaetProzent: number;
}

export type PlanungsBasisDatenTyp = 'start' | 'aktuell';

export interface PlanungsSzenario {
    id: string;
    timestamp: number;
    titel: string;
    basisDatenTyp: PlanungsBasisDatenTyp;
    prognoseDaten: PEProfitPrognose;
    sollPnlSnapshot: PEPnlBlock;
    /** Persistierter Banker-Pitch (Dummy/KI-Text) für dieses Szenario */
    bankerPitchText?: string;
}

export interface SollIstVergleichRow {
    key: string;
    label: string;
    soll: number;
    ist: number;
    delta: number;
    zielErreicht: boolean;
    isPercent?: boolean;
    isCost?: boolean;
}

export interface PEProfitBerechnungsMeta {
    preisAnpassungProzent: number;
    kundenVerlustProzent: number;
    materialKostenAnpassungProzent: number;
    zusaetzlicherUmsatzJahr: number;
    personalkostenDeltaMonat: number;
    investitionenMonat: number;
    umsatzJahr: { ist: number; prognose: number };
    /** Monats-IST Wareneinsatz (BWA) — für Detail-Aufstellung / Bank */
    materialIstMonat: number;
    /** Monats-Prognose Wareneinsatz — für Detail-Aufstellung / Bank */
    materialPrognoseMonat: number;
    /** Monats-IST Betriebskosten BWA-Summe (ohne Geräte) */
    betriebskostenIstMonat: number;
    /** Monats-Prognose Betriebskosten nach Einzelposten-Deltas (ohne Geräte) */
    betriebskostenPrognoseMonat: number;
    /** Monats-IST Personalkosten gesamt */
    personalkostenIstMonat: number;
    /** Monats-Prognose Personalkosten gesamt */
    personalkostenPrognoseMonat: number;
    /** Jahres-Zusatzumsatz durch neue Mitarbeiter (Personal-Dynamik) */
    umsatzNeuMitarbeiterJahr?: number;
    /** Jahres-Umsatzverlust durch Entlassungen (nach Auffang) */
    umsatzEntlassungVerlustJahr?: number;
    /** Monats-Materialzuschlag durch neue Mitarbeiter (IST-Quote) */
    materialZusatzNeuMitarbeiterMonat?: number;
    /** IST Unternehmerlohn/Entnahme (Monat) */
    unternehmerLohnIstMonat?: number;
    /** Prognose Unternehmerlohn/Entnahme (Monat) */
    unternehmerLohnPrognoseMonat?: number;
    /** Rohes Unternehmer-Umsatz-Delta (Jahr), vor Auffang */
    unternehmerUmsatzDeltaJahr?: number;
    /** Netto-Effekt auf Gesamtumsatz (Jahr) nach Schalter + Auffang */
    unternehmerUmsatzNettoAufGesamtJahr?: number;
}

export interface PEProfitCalculations {
    ready: boolean;
    zeitraum: number;
    szenarioName: string;
    ansichtsModus: PEProfitAnsichtsModus;
    ist: PEPnlBlock;
    prognose: PEPnlBlock;
    abweichung: PEPnlAbweichung;
    meta: PEProfitBerechnungsMeta;
    /** Store-Versionszähler — erzwingt konsistente Re-Renders */
    storeVersion?: number;
}
